import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { translate } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { AlertError, AlertSuccess } from '../../store/actions/shared.actions';
import { AppIntegrationRecord } from '../../models/integrations/appintegrationrecord.class';
import { IntegrationConfigField, IntegrationsSubSection, IntegrationTabStructure } from '../../interface/integrationmodal.interface';
import { IntegrationsUtilsService } from '../../services/integrations-utils/integrations-utils.service';
import { MasterTables } from '../../models/masters/mastertables.class';

@Component({
    selector: 'app-integration-postman-tab-stucture',
    templateUrl: './integration-postman-tab-stucture.component.html',
    styleUrls: ['./integration-postman-tab-stucture.component.scss'],
    standalone: false,
})
export class IntegrationPostmanTabStuctureComponent implements OnInit, OnDestroy {
    @Input() tab: IntegrationTabStructure;
    @Input() record: AppIntegrationRecord;
    @Input() hideField: (...args: any[]) => boolean;
    @Input() subSectionRecord: IntegrationsSubSection['records'][0];
    @Input() masterTableColumns: any;
    @Input() masterTables: MasterTables[];
    @Input() subTenants: any[];
    @Input() sources: { id: string; displayName: string; value?: string | boolean; pushValue?: string }[] = [];
    @Input() readonly = false;

    @Output() checkValidateButton = new EventEmitter<boolean>();
    @Output() checkDisableUpdateAndRunNow = new EventEmitter();
    @Output() testConnection = new EventEmitter();
    @Output() apiConfigChange = new EventEmitter();
    @Output() configNameChanged = new EventEmitter();
    @Output() getMasterColumns = new EventEmitter();
    @Output() chainChanged: EventEmitter<IntegrationConfigField['value']> = new EventEmitter();
    @Output() appChanged: EventEmitter<IntegrationConfigField['value']> = new EventEmitter();
    @Output() transformationsChanged: EventEmitter<IntegrationConfigField['value']> = new EventEmitter();

    constructor(private _integrationUtils: IntegrationsUtilsService, private store$: Store) {}

    addNewTabRecord = (currentTab: IntegrationTabStructure) => this._integrationUtils.addNewTabRecord(currentTab, this.record as any);

    /**
     * Checks if any input in the last row has content and adds a new empty row if needed
     */
    public checkAndAddNewRow = (): void => {
        if (this.isLastRowFilled()) {
            this.addNewTabRecord(this.tab);
        }
    };

    /**
     * Checks if the last row has any content in key, value, or description fields
     * @returns boolean indicating if the last row has content
     */
    private isLastRowFilled = (index?: number): boolean => {
        if (this.tab.records.length === 0) {
            return true;
        }
        const lastItem = this.tab.records[index !== undefined ? index : this.tab.records.length - 1];
        return !!lastItem.fields
            .filter(f => {
                const enableField =
                    !(f.disabled !== undefined ? f.disabled : false) &&
                    (f.conditionalDisable ? !this.record.canDisableField(f, undefined, lastItem) : true);
                const showField = f.conditionalDisplay ? this.record.canHideField(f, undefined, lastItem) : true;
                return enableField && showField;
            })
            .find(f => f.value !== '' && f.value !== undefined);
    };

    /**
     * Removes a header item at the specified index
     * @param index The index of the item to remove
     */
    public removeHeaderItem = (index: number): void => {
        if (index >= 0 && index < this.tab.records.length) {
            this.tab.records.splice(index, 1);

            // Make sure we always have at least one empty row at the end
            if (this.tab.records.length === 0 || this.isLastRowFilled()) {
                this.addNewTabRecord(this.tab);
            }
        }
    };

    deleteTabRecord = (currentTab: IntegrationTabStructure, index: number) =>
        this._integrationUtils.deleteTabRecord(currentTab, index, this.record as any);

    trackByFn = (index: number, _item: any): number => {
        return index;
    };

    checkExistingRows = () => {
        for (let i = this.tab.records.length - 1; i >= 0; i--) {
            if (!this.isLastRowFilled(i)) {
                this.removeHeaderItem(i);
            }
        }
    };

    /**
     * Copies the provided text to clipboard
     * @param text The text to copy
     */
    copyToClipboard(text: string): void {
        if (!text) return;
        const tempElement = document.createElement('div');
        tempElement.innerHTML = text;
        const cleanText = tempElement.textContent || tempElement.innerText || '';

        navigator.clipboard.writeText(cleanText).then(
            () => {
                this.store$.dispatch(
                    AlertSuccess({
                        message: translate('Copied to clipboard'),
                    })
                );
            },
            _err => {
                this.store$.dispatch(
                    AlertError({
                        message: translate('Failed to copy'),
                    })
                );
            }
        );
    }

    ngOnInit(): void {
        if (this.tab.structure === 'TABLE') {
            this.tab.array && this.checkExistingRows();
            this.tab.array && this.checkAndAddNewRow();
        }
    }

    ngOnDestroy(): void {}
}
